import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { task } from 'ember-concurrency';
import type { PowerCalendarArgs } from 'ember-power-calendar/components/power-calendar';
import PowerCalendar from 'ember-power-calendar/components/power-calendar';
// eslint-disable-next-line import-x/no-named-as-default
import powerCalendarFormatDate from 'ember-power-calendar/helpers/power-calendar-format-date';
import GeneralSmartInsightsCalendarDaysSmartInsights from 'lumen/components/general/smart-insights/calendar/days-smart-insights';
import GeneralSmartInsightsFooterSmartInsights from 'lumen/components/general/smart-insights/footer-smart-insights';
import type { SmartInsightDay } from 'lumen/models';
import { timeout } from 'lumen/utils/waiters';

interface GeneralSmartInsightsCalendarCalendarSmartInsightsSignature {
  Args: {
    insights: SmartInsightDay[];
    selectSmartInsightsDay: (day: string | null) => void;
  };
}

export default class GeneralSmartInsightsCalendarCalendarSmartInsights extends Component<GeneralSmartInsightsCalendarCalendarSmartInsightsSignature> {
  @service declare dates: Services['dates'];
  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];
  @service declare smartInsights: Services['smart-insights'];
  @service declare usageAnalytics: Services['usage-analytics'];

  tooltipHoverTrackingTask = task(
    { restartable: true },
    async (insight: SmartInsightDay): Promise<void> => {
      await timeout('extra-long');

      if (!insight) {
        return;
      }

      this.usageAnalytics.track('Smart Insight Previewed', {
        eventTrigger: 'smart-insights-calendar',
        product: 'smart-insights',
        ...this.smartInsights.getSmartInsightUsageAnalyticsContext(
          insight,
          insight.stayDate,
        ),
      });
    },
  );

  get calendarMonths(): PowerCalendarArgs['center'][] {
    const now = this.hotelAndMarketService.getLocalTime();
    const monthsMap = new Map();

    for (let i = 0; i <= this.smartInsights.leadtime; i++) {
      const dayMonth = now.add(i, 'day');
      monthsMap.set(dayMonth.format('YYYY-MM'), dayMonth);
    }

    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return Array.from(monthsMap.values());
  }

  <template>
    <div
      class="ic-product-tour-57546189-smart-insights-calendar smart-insights-calendar-view flex h-full flex-col"
      data-test-id="calendar-smart-insights"
    >
      <div
        class="smart-insights-calendar-view__calendar flex h-full flex-grow-1 flex-col overflow-auto"
      >
        {{#each this.calendarMonths as |calendarMonth|}}
          <div class="mb-600">
            <div class="flex items-center justify-center">
              <PowerCalendar
                @center={{calendarMonth}}
                @daysComponent={{GeneralSmartInsightsCalendarDaysSmartInsights}}
                @locale={{this.hotelAndMarketService.user.locale}}
                class="demo-calendar-medium smart-insights-calendar"
                data-test-id="smart-insight-calendar"
                as |calendar|
              >
                <calendar.Nav>
                  <div class="mb-200 flex gap-200">
                    <div
                      class="smart-insights-calendar__year paragraph-400--regular"
                    >
                      {{powerCalendarFormatDate
                        calendar.center
                        "YYYY"
                        locale=calendar.locale
                      }}
                    </div>
                    <h3 class="smart-insights-calendar__month">
                      {{powerCalendarFormatDate
                        calendar.center
                        "MMMM"
                        locale=calendar.locale
                      }}
                    </h3>
                  </div>
                </calendar.Nav>
                <calendar.Days
                  {{! @glint-expect-error: Incorrect type }}
                  @insights={{@insights}}
                  @selectSmartInsightsDay={{@selectSmartInsightsDay}}
                  @showDaysAround={{false}}
                  @startOfWeek={{this.dates.startOfWeek}}
                  @tooltipHoverTrackingTask={{this.tooltipHoverTrackingTask}}
                />
              </PowerCalendar>
            </div>
          </div>
        {{/each}}
      </div>

      <div class="smart-insights-calendar-view__footer flex-shrink-0">
        <GeneralSmartInsightsFooterSmartInsights />
      </div>
    </div>
  </template>
}
